import { AppIcon } from '@/components/ui/app-icon';
import { OrganizationSwitcher, useClerk } from '@clerk/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { FEATURE_MATRIX } from '@/constants/plan-feature-matrix';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOrganizationActionFence } from '@/hooks/use-organization-action-fence.hook';
import { usePageRestored } from '@/hooks/use-page-restored.hook';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import {
  CARD_TRIAL_DAYS,
  featuresLostOnStart,
  formatRenewalPrice,
  formatShortDate,
  proTrialTerms,
  termCount,
  TRIAL_CREDITS,
  type ProTrialTerms,
} from '@/lib/card-trial';
import {
  iconArrowRight,
  iconBell,
  iconBolt,
  iconCheck,
  iconCircleInfo,
  iconCreditCard,
  iconSpinnerThird,
  iconStarSolid,
  iconXmark,
} from '@/lib/icons';
import { redirectToStripeCheckout } from '@/lib/stripe-redirect';
import { cn } from '@/lib/utils';
import {
  useActivateTrialMutation,
  useGetPlansQuery,
} from '@/store/features/billing.feature';

import { ProTrialOffer } from './pro-trial-offer.component';
import { BRAND, brandAsset } from '@/lib/brand';

const DAY_MS = 24 * 60 * 60 * 1000;

// Where the 4.8 average comes from (G2 5.0 ×5, Chrome 4.7 ×13, AppSumo 4.8 ×112).
const REVIEW_SOURCES = [
  { src: '/logos/g2.png', name: 'G2' },
  { src: '/logos/chrome-web-store-mark.png', name: 'Chrome Web Store' },
  { src: '/logos/appsumo-mark.png', name: 'AppSumo' },
];

// Trial vs the plan it becomes and Free, from the /plans matrix so the two
// can't drift: a few shared basics, then what only the trial has.
const SHARED_FEATURES = [
  'Enrichment from 15+ providers',
  'AI Search',
  'Chrome Extension',
];
const TRIAL_ONLY_FEATURES = featuresLostOnStart('starter');
const COMPARISON = FEATURE_MATRIX.filter(
  (row) =>
    SHARED_FEATURES.includes(row.feature) ||
    TRIAL_ONLY_FEATURES.includes(row.feature)
).map((row) => ({
  feature: row.feature,
  starter: row.starter === true,
  free: row.free === true,
}));

// Legacy Free Tier's monthly refill (config PRODUCT_TIERS "Free Tier"
// count), for the Scale trial only. The Pro trial reads v4 Free's grant
// from the API (ProTrialTerms).
const FREE_MONTHLY_CREDITS = 30;

// Metered rather than on/off: search_allowance.py TRIAL_SEARCH_GRANT and the
// free preview quota (PREVIEW_QUOTA_LIMIT).
const CELL_TEXT: Record<string, { trial?: string; free?: string }> = {
  'AI Search': { trial: '100 searches', free: '5 searches' },
};

/**
 * Card-required trial offer: full screen after onboarding, and in a dialog
 * from the offer banner (`onClose`). Continuing on Free keeps the offer open.
 */
export function TrialActivateGate({
  cancelled = false,
  onContinueFree,
  onClose,
}: {
  /** Back from a Stripe Checkout the user closed (`?trial=cancelled`). */
  cancelled?: boolean;
  /** Continue on Free, after the skip confirmation. */
  onContinueFree?: () => void;
  /** Opened from the banner: close instead of org switch, sign out and skip. */
  onClose?: () => void;
}) {
  const { trial, organizationId, catalogFamily } = useFeatureAccess();
  const posthog = usePostHog();
  const clerk = useClerk();
  const { beginOrganizationAction, isOrganizationActionCurrent } =
    useOrganizationActionFence(organizationId);
  const [activateTrial] = useActivateTrialMutation();
  const [pending, setPending] = useState<'trial' | null>(null);
  const [confirmFree, setConfirmFree] = useState(false);
  const viewedRef = useRef(false);
  const fromBanner = onClose !== undefined;
  const source = fromBanner ? 'banner' : 'onboarding';
  const proTrial = trial?.plan === 'pro' || catalogFamily === 'v4';
  // The Pro trial page's numbers come from the API: the trial block above and
  // the v4 plan catalog.
  const { currentData: plans } = useGetPlansQuery(
    { organizationId },
    { skip: !proTrial || !organizationId }
  );
  const terms = useMemo(
    () => proTrialTerms(trial, plans?.plans),
    [trial, plans]
  );
  const trialPlan = proTrial ? 'pro' : 'scale';

  // Once the trial payload is in, so `trial_plan` names the offer shown.
  const trialLoaded = trial != null;
  useEffect(() => {
    if (viewedRef.current || !trialLoaded) return;
    viewedRef.current = true;
    bestEffortAnalytics(() =>
      posthog?.capture('card_trial_offer_viewed', {
        source,
        trial_plan: trialPlan,
      })
    );
  }, [posthog, source, trialPlan, trialLoaded]);

  const renewal = trial?.renews_to;
  const credits = trial?.credits_granted ?? TRIAL_CREDITS;
  const days = trial?.days_remaining ?? CARD_TRIAL_DAYS;
  const startsAt = new Date(Date.now() + days * DAY_MS);
  const renewalDay = formatShortDate(startsAt);
  const reminderDay = formatShortDate(startsAt.getTime() - 3 * DAY_MS);
  const planLabel = renewal?.label ?? 'Starter';
  const planPrice = renewal ? formatRenewalPrice(renewal) : '';

  // Browser Back from Checkout: the page returns with the spinner still on.
  usePageRestored(() => setPending(null));

  const handleStart = async (acceptEarlyConversion = false) => {
    const action = beginOrganizationAction();
    if (!action || pending) return;
    setPending('trial');
    bestEffortAnalytics(() =>
      posthog?.capture('card_trial_start_clicked', {
        source,
        trial_plan: trialPlan,
      })
    );
    try {
      const response = await activateTrial({
        organizationId: action.organizationId,
        ...(acceptEarlyConversion ? { accept_early_conversion: true } : {}),
      }).unwrap();
      const redirected = await redirectToStripeCheckout(response, () =>
        isOrganizationActionCurrent(action)
      );
      if (isOrganizationActionCurrent(action) && !redirected) setPending(null);
    } catch (error) {
      if (!isOrganizationActionCurrent(action)) return;
      toast.error(
        (error as { data?: { detail?: string } })?.data?.detail ||
          'Could not open checkout. Please try again.'
      );
      setPending(null);
    }
  };

  const handleFree = () => {
    bestEffortAnalytics(() =>
      posthog?.capture('card_trial_free_selected', { trial_plan: trialPlan })
    );
    setConfirmFree(false);
    onContinueFree?.();
    onClose?.();
  };

  const timeline = [
    {
      icon: iconBolt,
      when: 'Today',
      title: 'Enterprise unlocked, free',
      body: `Advanced agents, playbooks and CRM sync, with ${credits} credits and 3 seats.`,
    },
    {
      icon: iconBell,
      when: reminderDay,
      title: 'A reminder in your inbox',
      body: 'Three days before your trial ends, so nothing sneaks up on you.',
    },
    {
      icon: iconCreditCard,
      when: renewalDay,
      title: `You move to ${planLabel}${planPrice ? ` at ${planPrice}` : ''}`,
      body: 'Unless you pick Pro or Enterprise, or cancel. Both take two clicks in Plans.',
    },
  ];

  const promptFree = () => {
    setConfirmFree(true);
    bestEffortAnalytics(() =>
      posthog?.capture('card_trial_free_prompt_viewed', {
        trial_plan: trialPlan,
      })
    );
  };

  const headerActions = fromBanner ? (
    <Button
      variant='ghost'
      size='icon'
      className='text-muted-foreground'
      aria-label='Close'
      onClick={onClose}
    >
      <AppIcon icon={iconXmark} className='h-4 w-4' />
    </Button>
  ) : (
    <div className='flex items-center gap-1'>
      <OrganizationSwitcher hidePersonal />
      <Button
        variant='ghost'
        size='sm'
        className='text-xs text-muted-foreground'
        onClick={() => void clerk.signOut()}
      >
        Sign out
      </Button>
    </div>
  );

  const skipDialog = !fromBanner && (
    <SkipTrialDialog
      open={confirmFree}
      onOpenChange={setConfirmFree}
      credits={credits}
      proTrial={proTrial}
      terms={terms}
      pending={pending}
      onStartTrial={() => {
        setConfirmFree(false);
        void handleStart();
      }}
      onContinueFree={handleFree}
    />
  );

  // The banner's dialog sits over the app's own <main>.
  const Root = fromBanner ? 'div' : 'main';

  if (proTrial) {
    return (
      <>
        <ProTrialOffer
          renewal={renewal}
          terms={terms}
          busy={pending !== null}
          cancelled={cancelled}
          inDialog={fromBanner}
          headerActions={headerActions}
          continueLabel={
            fromBanner
              ? 'Not now, stay on Free'
              : 'Continue on the Free plan instead'
          }
          onStart={() => void handleStart()}
          onContinueFree={fromBanner ? onClose : promptFree}
        />
        {skipDialog}
      </>
    );
  }

  return (
    <Root
      className='flex min-h-dvh flex-col bg-background lg:flex-row'
      aria-labelledby='trial-activate-title'
      aria-busy={pending !== null}
    >
      <section className='flex flex-1 flex-col px-6 py-6 sm:px-10 lg:px-16'>
        <header className='flex items-center justify-between gap-3'>
          <img
            src={brandAsset('/logo-dark.png')}
            alt={BRAND.name}
            className='h-8 w-auto dark:hidden'
          />
          <img
            src={brandAsset('/logo-light.png')}
            alt={BRAND.name}
            className='hidden h-8 w-auto dark:block'
          />
          {headerActions}
        </header>

        <div className='flex flex-1 items-center justify-center py-10'>
          <div className='w-full max-w-[460px]'>
            <h1
              id='trial-activate-title'
              className='text-[38px] leading-[1.08] font-semibold tracking-[-0.035em] text-foreground sm:text-[44px]'
            >
              Try {BRAND.name} Enterprise free for {days} days
            </h1>
            <p className='mt-4 text-[17px] leading-7 text-muted-foreground'>
              Nothing is charged today. Switch plans or cancel anytime from
              inside the app.
            </p>

            <ol className='mt-9'>
              {timeline.map((step, i) => (
                <li
                  key={step.when}
                  className='relative flex gap-4 pb-6 last:pb-0'
                >
                  {i < timeline.length - 1 && (
                    <span
                      aria-hidden='true'
                      className={cn(
                        'absolute top-10 bottom-1 left-[17px] w-0.5 rounded-full',
                        i === 0
                          ? 'bg-linear-to-b from-primary to-primary/30'
                          : 'bg-border'
                      )}
                    />
                  )}
                  <span
                    className={cn(
                      'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                      i === 0
                        ? 'bg-primary text-primary-foreground shadow-md shadow-primary/30'
                        : 'bg-muted text-muted-foreground ring-1 ring-border'
                    )}
                  >
                    <AppIcon icon={step.icon} className='h-3.5 w-3.5' />
                  </span>
                  <div className='pt-0.5'>
                    <p
                      className={cn(
                        'text-xs font-medium',
                        i === 0 ? 'text-primary' : 'text-muted-foreground'
                      )}
                    >
                      {step.when}
                    </p>
                    <p className='mt-0.5 text-[15px] font-semibold text-foreground'>
                      {step.title}
                    </p>
                    <p className='mt-0.5 text-sm leading-6 text-muted-foreground'>
                      {step.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            {cancelled && (
              <p
                role='status'
                className='mt-8 flex items-center gap-2 text-[13px] text-muted-foreground'
              >
                <AppIcon icon={iconCircleInfo} className='h-3.5 w-3.5' />
                Checkout was closed. Nothing was charged.
              </p>
            )}

            <Button
              variant='brand'
              className={cn(
                'h-[52px] w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/25',
                cancelled ? 'mt-4' : 'mt-9'
              )}
              onClick={() => void handleStart()}
              disabled={pending !== null}
            >
              {pending === 'trial' ? (
                <AppIcon icon={iconSpinnerThird} spin />
              ) : null}
              Start my free trial
              {pending !== 'trial' && (
                <AppIcon icon={iconArrowRight} className='h-3.5 w-3.5' />
              )}
            </Button>
            <p className='mt-3 text-center text-[13px] text-muted-foreground'>
              $0 due today · Secured by Stripe · Cancel in two clicks
            </p>

            <div className='mt-8 flex items-center gap-3 border-t border-border pt-7'>
              <div className='flex -space-x-2'>
                {REVIEW_SOURCES.map((source) => (
                  <span
                    key={source.name}
                    className='flex h-9 w-9 items-center justify-center rounded-full bg-background ring-2 ring-background'
                    title={source.name}
                  >
                    <img
                      src={source.src}
                      alt={source.name}
                      className='h-5 w-5 object-contain'
                    />
                  </span>
                ))}
              </div>
              <div>
                <p className='flex items-center gap-1.5 text-sm font-semibold text-foreground'>
                  <span
                    className='flex gap-0.5 text-[#ffb020]'
                    aria-hidden='true'
                  >
                    {Array.from({ length: 5 }, (_, i) => (
                      <AppIcon
                        key={i}
                        icon={iconStarSolid}
                        className='h-3 w-3'
                      />
                    ))}
                  </span>
                  4.8 out of 5
                </p>
                <p className='text-[13px] text-muted-foreground'>
                  From 130 reviews · Used by 2,000+ GTM teams
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className='pt-2 text-center'>
          {fromBanner ? (
            <button
              type='button'
              onClick={onClose}
              disabled={pending !== null}
              className='text-[13px] text-muted-foreground/70 underline-offset-4 hover:text-muted-foreground hover:underline disabled:opacity-50'
            >
              Not now, stay on Free
            </button>
          ) : (
            <button
              type='button'
              onClick={promptFree}
              disabled={pending !== null}
              className='text-[13px] text-muted-foreground/70 underline-offset-4 hover:text-muted-foreground hover:underline disabled:opacity-50'
            >
              Continue on the Free plan instead
            </button>
          )}
        </div>
      </section>

      {skipDialog}

      <TrialComparison
        credits={credits}
        days={days}
        planLabel={planLabel}
        planPrice={planPrice}
        planCredits={renewal?.credits}
        renewalDay={renewalDay}
      />
    </Root>
  );
}

function Tick({ on, highlight }: { on: boolean; highlight?: boolean }) {
  if (!on) {
    return (
      <span className='text-white/25'>
        <span aria-hidden='true'>—</span>
        <span className='sr-only'>Not included</span>
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center rounded-full',
        highlight ? 'bg-[#316bff] text-white' : 'bg-white/10 text-white/70'
      )}
    >
      <AppIcon icon={iconCheck} className='h-2.5 w-2.5' />
      <span className='sr-only'>Included</span>
    </span>
  );
}

function TrialComparison({
  credits,
  days,
  planLabel: label,
  planPrice,
  planCredits,
  renewalDay,
}: {
  credits: number;
  days: number;
  planLabel: string;
  planPrice: string;
  planCredits?: number;
  renewalDay: string;
}) {
  const cell =
    'flex items-center justify-center border-t border-white/[0.07] py-2';

  return (
    <aside className='relative overflow-hidden bg-[#0b0d12] px-6 py-12 text-white sm:px-10 lg:flex lg:w-[46%] lg:items-center lg:px-14'>
      <div
        aria-hidden='true'
        className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_70%_0%,rgba(49,107,255,0.28),transparent_70%)]'
      />
      <div className='relative mx-auto w-full max-w-[500px]'>
        <h2 className='text-2xl font-semibold tracking-[-0.02em]'>
          Your trial vs. {label} and Free
        </h2>
        <p className='mt-2 text-sm leading-6 text-white/60'>
          Your workspace moves to {label} on {renewalDay} unless you choose
          another plan. Here&apos;s what changes.
        </p>

        <div className='mt-7 overflow-hidden rounded-2xl bg-white/[0.03] ring-1 ring-white/10'>
          <div className='grid grid-cols-[1fr_96px_88px_72px] text-sm'>
            <div />
            <div className='bg-[#316bff]/15 px-2 pt-4 pb-3 text-center'>
              <p className='font-semibold'>Enterprise trial</p>
              <p className='mt-0.5 text-xs font-medium text-[#8fb6ff]'>
                Free · {days} days
              </p>
            </div>
            <div className='px-2 pt-4 pb-3 text-center'>
              <p className='font-semibold text-white/80'>{label}</p>
              <p className='mt-0.5 text-xs text-white/50'>
                {planPrice || 'Paid'}
              </p>
            </div>
            <div className='px-2 pt-4 pb-3 text-center'>
              <p className='font-semibold text-white/80'>Free</p>
              <p className='mt-0.5 text-xs text-white/50'>$0</p>
            </div>

            <div className='flex items-center border-t border-white/[0.07] px-4 py-2 text-white/75'>
              Credits
            </div>
            <div className={cn(cell, 'bg-[#316bff]/15 font-semibold')}>
              {credits.toLocaleString('en-US')}
            </div>
            <div className={cn(cell, 'text-xs text-white/70')}>
              {planCredits ? `${planCredits.toLocaleString('en-US')}/mo` : '—'}
            </div>
            <div className={cn(cell, 'text-xs text-white/60')}>
              {FREE_MONTHLY_CREDITS}/mo
            </div>

            {COMPARISON.map((row) => (
              <div key={row.feature} className='contents'>
                <div className='flex items-center border-t border-white/[0.07] px-4 py-2 text-white/75'>
                  {row.feature}
                </div>
                <div
                  className={cn(cell, 'bg-[#316bff]/15 text-xs font-medium')}
                >
                  {CELL_TEXT[row.feature]?.trial ?? <Tick on highlight />}
                </div>
                <div className={cell}>
                  <Tick on={row.starter} />
                </div>
                <div className={cn(cell, 'text-xs text-white/60')}>
                  {CELL_TEXT[row.feature]?.free ?? <Tick on={row.free} />}
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className='mt-6 text-[13px] leading-6 text-white/55'>
          Want to keep the Enterprise features? Pick Pro or Enterprise in Plans
          any time before {renewalDay}. Your lists, enrichments and credits
          carry over.
        </p>
        <p className='mt-3 text-xs leading-5 text-white/40'>
          During the trial: up to 5 lists of 300 leads each. API and MCP access
          start on a paid plan.
        </p>
      </div>
    </aside>
  );
}

function SkipTrialDialog({
  open,
  onOpenChange,
  credits,
  proTrial,
  terms,
  pending,
  onStartTrial,
  onContinueFree,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credits: number;
  proTrial: boolean;
  /** The Pro trial's and v4 Free's numbers, from the API. */
  terms: ProTrialTerms;
  pending: 'trial' | null;
  onStartTrial: () => void;
  onContinueFree: () => void;
}) {
  const losses = proTrial
    ? [
        {
          trial: `${termCount(terms.trialCredits)} credits`,
          free: `${termCount(terms.freeCredits)} a month`,
        },
        {
          trial: `${termCount(terms.trialSearches)} searches`,
          free: `${termCount(terms.freeSearches)} on Free`,
        },
        { trial: 'AI agents and LinkedIn steps', free: 'Not on Free' },
        { trial: 'Email and call sequences', free: 'Not on Free' },
        { trial: 'Native CRM sync', free: 'Not on Free' },
      ]
    : [
        {
          trial: `${credits} credits`,
          free: `${FREE_MONTHLY_CREDITS} a month`,
        },
        { trial: '100 AI searches', free: '5 on Free' },
        { trial: '3 seats for your team', free: '1 on Free' },
        { trial: 'Advanced AI agents and playbooks', free: 'Not on Free' },
        { trial: 'Native CRM sync and CRM import', free: 'Not on Free' },
      ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[440px] gap-0 overflow-hidden rounded-2xl p-0'>
        <div className='bg-linear-to-b from-primary/12 to-transparent px-7 pt-8 pb-2'>
          <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/30'>
            <AppIcon icon={iconBolt} className='h-4 w-4' />
          </span>
          <DialogTitle className='mt-5 text-xl font-semibold tracking-[-0.02em]'>
            Skip your free {proTrial ? 'Pro' : 'Enterprise'} trial?
          </DialogTitle>
          <DialogDescription className='mt-1.5 text-sm leading-6 text-muted-foreground'>
            It costs nothing today and you can cancel anytime. On Free
            you&apos;d miss out on:
          </DialogDescription>
        </div>

        <ul className='mx-7 mt-4 divide-y divide-border rounded-xl border border-border'>
          {losses.map((loss) => (
            <li
              key={loss.trial}
              className='flex items-center justify-between gap-3 px-4 py-2.5 text-sm'
            >
              <span className='flex items-center gap-2.5 font-medium text-foreground'>
                <AppIcon icon={iconCheck} className='h-3 w-3 text-primary' />
                {loss.trial}
              </span>
              <span className='shrink-0 text-xs text-muted-foreground'>
                {loss.free}
              </span>
            </li>
          ))}
        </ul>

        <div className='px-7 pt-6 pb-7'>
          <Button
            variant='brand'
            className='h-11 w-full rounded-xl text-[15px] font-semibold'
            onClick={onStartTrial}
            disabled={pending !== null}
            autoFocus
          >
            Start my free trial instead
          </Button>
          <button
            type='button'
            onClick={onContinueFree}
            disabled={pending !== null}
            className='mt-3 w-full py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50'
          >
            Yes, continue on Free
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
